import { ImageIcon, Package, Plus, TrashIcon } from "@/components/icons";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DatePicker,
  Input,
  Label,
  Separator,
  Skeleton,
  Badge,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import type { CreateImportOrderDTO } from "@/modules/inventory/domain/import-order-contracts";
import {
  estimateExpectedArrivalDateInput,
  findImportArrivalEstimate,
} from "@/modules/inventory/domain/lib/import-arrival-estimate";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import {
  convertReceiveQtyToStock,
  formatQtyWithUnit,
  normalizeStockUnitLabel,
  usesAlternateReceiveUnit,
} from "@/modules/products/domain/unit-conversion";
import { formatDateForInput } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useImportArrivalEstimates,
  usePreviewImportOrderLandedCost,
} from "../api/import-orders-queries";
import { saveImportOrderPhase13Draft } from "../lib/import-prefill-phase13";
import { enrichImportLinesWithLastPrices } from "../lib/apply-last-import-prices";
import {
  importLineFormToApiInput,
  isMatchedImportLineForm,
  isPendingImportLineForm,
  lineFormIsValid,
  newPendingImportLineForm,
  normalizeImportLineForm,
  type ImportOrderPhase13LineForm,
  type ImportOrderPhase13MatchedLineForm,
  type ImportOrderPhase13PendingLineForm,
} from "../lib/import-order-line-form";
import {
  IMPORT_ORDER_CURRENCY_OPTIONS,
  IMPORT_ORDER_SOURCE_LABELS,
  formatLakAmount,
  importOrderPriceUnitLabel,
} from "../lib/import-order-labels";

export type {
  ImportOrderPhase13LineForm,
  ImportOrderPhase13MatchedLineForm,
  ImportOrderPhase13PendingLineForm,
} from "../lib/import-order-line-form";

export type ImportOrderPhase13FormValues = {
  externalOrderNo: string;
  source: CreateImportOrderDTO["source"];
  currency: CreateImportOrderDTO["currency"];
  exchangeRate: number;
  shippingTotalLak: number;
  orderedAt: string;
  expectedArrivalDate: string;
  note: string;
  trackingNo: string;
  carrierName: string;
  markOrdered: boolean;
  lines: ImportOrderPhase13LineForm[];
};

const SOURCE_OPTIONS = (
  Object.keys(IMPORT_ORDER_SOURCE_LABELS) as CreateImportOrderDTO["source"][]
).map((id) => ({
  value: id,
  label: IMPORT_ORDER_SOURCE_LABELS[id],
}));

function emptyValues(): ImportOrderPhase13FormValues {
  return {
    externalOrderNo: "",
    source: "taobao",
    currency: "LAK",
    exchangeRate: 1,
    shippingTotalLak: 0,
    orderedAt: "",
    expectedArrivalDate: "",
    note: "",
    trackingNo: "",
    carrierName: "",
    markOrdered: false,
    lines: [],
  };
}

function normalizeLines(
  lines: ImportOrderPhase13LineForm[],
): Array<ImportOrderPhase13MatchedLineForm | ImportOrderPhase13PendingLineForm> {
  return lines.map(normalizeImportLineForm);
}

type ImportOrderPhase13FormProps = {
  defaultValues?: Partial<ImportOrderPhase13FormValues>;
  defaultExchangeRate?: number;
  /** ລັອກທັງໝົດ (ລວມລາຄາ/ຈຳນວນ) */
  readOnlyLines?: boolean;
  /** ລັອກເພີ່ມ/ລຶບລາຍການ — ຍັງແກ້ຈຳນວນ ລາຄາ ແລະ ຂົນສົ່ງໄດ້ */
  readOnlyLineStructure?: boolean;
  submitting?: boolean;
  submitLabel?: string;
  /** Phase 25 — ກັບມາ prefill ຫຼັງສ້າງສິນຄ້າ (ເຊັ່ນ /app/import-orders/new) */
  productCreateReturnTo?: string;
  onSubmit: (
    values: CreateImportOrderDTO,
    formLines: Array<
      ImportOrderPhase13MatchedLineForm | ImportOrderPhase13PendingLineForm
    >,
  ) => Promise<void> | void;
};

export function ImportOrderPhase13Form({
  defaultValues,
  defaultExchangeRate,
  readOnlyLines = false,
  readOnlyLineStructure = false,
  submitting,
  submitLabel = "ບັນທຶກໃບສັ່ງ",
  productCreateReturnTo,
  onSubmit,
}: ImportOrderPhase13FormProps) {
  const nav = useNavigate();
  const arrivalEstimates = useImportArrivalEstimates();
  const [values, setValues] = useState<ImportOrderPhase13FormValues>({
    ...emptyValues(),
    exchangeRate:
      defaultValues?.currency === "CNY"
        ? (defaultExchangeRate ?? 3500)
        : emptyValues().exchangeRate,
    ...defaultValues,
    lines: normalizeLines(defaultValues?.lines ?? []),
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const previewMutation = usePreviewImportOrderLandedCost();
  const lastPricesAppliedRef = useRef<string>("");
  const expectedArrivalTouchedRef = useRef(
    Boolean(defaultValues?.expectedArrivalDate?.trim()),
  );

  const activeArrivalEstimate = useMemo(
    () =>
      findImportArrivalEstimate(
        arrivalEstimates.data?.bySource ?? [],
        values.source,
      ),
    [arrivalEstimates.data?.bySource, values.source],
  );

  useEffect(() => {
    if (readOnlyLines || expectedArrivalTouchedRef.current) return;
    if (!activeArrivalEstimate) return;

    const nextExpected = estimateExpectedArrivalDateInput(
      values.orderedAt,
      activeArrivalEstimate.medianLeadDays,
    );

    setValues((prev) => {
      if (prev.expectedArrivalDate === nextExpected) return prev;
      return { ...prev, expectedArrivalDate: nextExpected };
    });
  }, [
    activeArrivalEstimate,
    readOnlyLines,
    values.orderedAt,
    values.source,
  ]);

  useEffect(() => {
    if (defaultExchangeRate == null) return;
    setValues((prev) => {
      if (prev.currency === "LAK") return prev;
      return {
        ...prev,
        exchangeRate: defaultExchangeRate,
      };
    });
  }, [defaultExchangeRate]);

  useEffect(() => {
    const matchedLines = values.lines.filter(isMatchedImportLineForm);
    const variantKey = matchedLines.map((line) => line.variantId).join(",");
    if (!variantKey || lastPricesAppliedRef.current === variantKey) return;

    let cancelled = false;
    void enrichImportLinesWithLastPrices(matchedLines).then((lines) => {
      if (cancelled) return;
      lastPricesAppliedRef.current = variantKey;
      setValues((prev) => {
        const prevMatched = prev.lines.filter(isMatchedImportLineForm);
        if (prevMatched.map((line) => line.variantId).join(",") !== variantKey) {
          return prev;
        }
        const enrichedByVariant = new Map(
          lines.map((line) => [line.variantId, line]),
        );
        return {
          ...prev,
          lines: prev.lines.map((line) => {
            if (!isMatchedImportLineForm(line)) return line;
            return enrichedByVariant.get(line.variantId) ?? line;
          }),
        };
      });
    });

    return () => {
      cancelled = true;
    };
  }, [values.lines]);

  const isLakCurrency = values.currency === "LAK";
  const priceUnit = importOrderPriceUnitLabel(values.currency);
  const effectiveExchangeRate = isLakCurrency ? 1 : values.exchangeRate;

  const previewInput = useMemo(
    () => ({
      currency: values.currency,
      exchangeRate: effectiveExchangeRate,
      shippingTotalLak: values.shippingTotalLak,
      lines: values.lines
        .filter(isMatchedImportLineForm)
        .map((l) => ({
          variantId: l.variantId,
          productName: l.productName,
          variantName: l.variantName,
          description: l.description || null,
          orderedQuantity: l.orderedQuantity,
          unitPriceForeign: l.unitPriceForeign,
        })),
    }),
    [effectiveExchangeRate, values],
  );

  useEffect(() => {
    if (previewInput.lines.length === 0) return;
    const timer = setTimeout(() => {
      previewMutation.mutate(previewInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [previewInput]);

  const preview = previewMutation.data;
  const lockStructure = readOnlyLines || readOnlyLineStructure;
  const lockPrices = readOnlyLines;
  const lockQuantity = readOnlyLines;

  function patch(patchValues: Partial<ImportOrderPhase13FormValues>) {
    setValues((v) => ({ ...v, ...patchValues }));
  }

  function handleCreateProduct() {
    if (!productCreateReturnTo) return;
    saveImportOrderPhase13Draft({
      returnTo: productCreateReturnTo,
      values,
    });
    nav({ to: "/app/products/new" });
  }

  function handlePick(picked: PickedVariant) {
    setValues((v) => {
      const existing = v.lines.find(
        (l) => isMatchedImportLineForm(l) && l.variantId === picked.variantId,
      );
      if (existing && isMatchedImportLineForm(existing)) {
        return {
          ...v,
          lines: v.lines.map((l) =>
            isMatchedImportLineForm(l) && l.variantId === picked.variantId
              ? { ...l, orderedQuantity: l.orderedQuantity + 1 }
              : l,
          ),
        };
      }
      return {
        ...v,
        lines: [
          ...v.lines,
          {
            kind: "matched" as const,
            lineKey: `new-${picked.variantId}`,
            variantId: picked.variantId,
            productName: picked.productName,
            variantName: picked.variantName,
            imageKey: picked.imageKey,
            description: "",
            orderedQuantity: 1,
            unitPriceForeign: 0,
            stockUnitLabel: picked.stockUnitLabel,
            receiveUnitLabel: picked.receiveUnitLabel,
            receiveConversionFactor:
              Number(picked.receiveConversionFactor) || 1,
          },
        ],
      };
    });
  }

  function addPendingLine() {
    setValues((v) => ({
      ...v,
      lines: [...v.lines, newPendingImportLineForm()],
    }));
  }

  function updateLine(
    lineKey: string,
    patchLine: Partial<ImportOrderPhase13LineForm>,
  ) {
    setValues((v) => ({
      ...v,
      lines: v.lines.map((l) =>
        (isPendingImportLineForm(l) || isMatchedImportLineForm(l)) &&
        l.lineKey === lineKey
          ? ({ ...l, ...patchLine } as ImportOrderPhase13LineForm)
          : l,
      ),
    }));
  }

  function removeLine(lineKey: string) {
    setValues((v) => ({
      ...v,
      lines: v.lines.filter((l) => {
        if (isPendingImportLineForm(l) || isMatchedImportLineForm(l)) {
          return l.lineKey !== lineKey;
        }
        return true;
      }),
    }));
  }

  const canSubmit =
    values.externalOrderNo.trim().length > 0 &&
    (isLakCurrency || values.exchangeRate > 0) &&
    values.lines.length > 0 &&
    values.lines.every(lineFormIsValid) &&
    !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    await onSubmit(
      {
        externalOrderNo: values.externalOrderNo.trim(),
        source: values.source,
        currency: values.currency,
        exchangeRate: effectiveExchangeRate,
        shippingTotalLak: values.shippingTotalLak,
        orderedAt: values.orderedAt || null,
        expectedArrivalDate: values.expectedArrivalDate || null,
        note: values.note.trim() || null,
        status: values.markOrdered ? "ordered" : "draft",
        lines: normalizeLines(values.lines).map(importLineFormToApiInput),
        trackings: values.trackingNo.trim()
          ? [
              {
                trackingNo: values.trackingNo.trim(),
                carrierName: values.carrierName.trim() || null,
              },
            ]
          : [],
      },
      normalizeLines(values.lines),
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <Card className="brand-card">
        <CardContent className="!py-0 flex flex-col gap-4 px-4 sm:px-5">
          <div>
            <h3 className="font-display text-lg">ຂໍ້ມູນ order</h3>
            <p className="text-muted-foreground text-sm">
              ເລກອໍເດີ້ຈາກ Taobao / 1688 / Pinduoduo + ສະກຸນເງິນ
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="externalOrderNo">ເລກ order ຈາກແອບ *</Label>
              <Input
                id="externalOrderNo"
                value={values.externalOrderNo}
                onChange={(e) => patch({ externalOrderNo: e.target.value })}
                placeholder="TB-2026-001"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>ແພລດຟອມ</Label>
              <SimpleSelect
                value={values.source}
                onValueChange={(v) =>
                  patch({ source: v as ImportOrderPhase13FormValues["source"] })
                }
                options={SOURCE_OPTIONS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>ສະກຸນເງິນ *</Label>
              <SimpleSelect
                value={values.currency}
                onValueChange={(v) => {
                  const currency = v as ImportOrderPhase13FormValues["currency"];
                  if (currency === "LAK") {
                    patch({ currency, exchangeRate: 1 });
                    return;
                  }
                  patch({
                    currency,
                    exchangeRate:
                      values.exchangeRate > 1
                        ? values.exchangeRate
                        : (defaultExchangeRate ?? 3500),
                  });
                }}
                options={IMPORT_ORDER_CURRENCY_OPTIONS}
              />
            </div>
            {!isLakCurrency ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="exchangeRate">ອັດຕາແລກປ່ຽນ (CNY→LAK) *</Label>
                <Input
                  id="exchangeRate"
                  type="number"
                  min={0}
                  step="0.0001"
                  value={values.exchangeRate || ""}
                  onChange={(e) =>
                    patch({ exchangeRate: Number(e.target.value) || 0 })
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label>ອັດຕາແລກປ່ຽນ</Label>
                <p className="text-muted-foreground rounded-lg border border-dashed px-3 py-2 text-sm">
                  ລາຄາເປັນ LAK ແລ້ວ — ບໍ່ຄຳນວນອັດຕາແລກປ່ຽນ
                </p>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="orderedAt">ວັນທີສັ່ງ</Label>
              <DatePicker
                value={values.orderedAt ? new Date(values.orderedAt) : null}
                onChange={(date) =>
                  patch({
                    orderedAt: date ? formatDateForInput(date) : "",
                  })
                }
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expectedArrivalDate">ຄາດວ່າຮອດ</Label>
              <DatePicker
                value={
                  values.expectedArrivalDate
                    ? new Date(values.expectedArrivalDate)
                    : null
                }
                onChange={(date) => {
                  expectedArrivalTouchedRef.current = true;
                  patch({
                    expectedArrivalDate: date ? formatDateForInput(date) : "",
                  });
                }}
                className="w-full"
              />
              {activeArrivalEstimate && !readOnlyLines ? (
                <p className="text-muted-foreground text-xs">
                  {activeArrivalEstimate.isFallback
                    ? `ຄາດ ~${activeArrivalEstimate.medianLeadDays} ວັນ (${IMPORT_ORDER_SOURCE_LABELS[values.source]} — ຄ່າເລີ່ມຕົ້ນ)`
                    : `ຄາດ ~${activeArrivalEstimate.medianLeadDays} ວັນ ຈາກ ${activeArrivalEstimate.sampleCount} ໃບສັ່ງ ${IMPORT_ORDER_SOURCE_LABELS[values.source]} ກ່ອນໜ້າ`}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="note">ໝາຍເຫດ</Label>
              <Input
                id="note"
                value={values.note}
                onChange={(e) => patch({ note: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="brand-card">
        <CardContent className="flex flex-col gap-3 px-4 py-0 sm:px-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-lg">ລາຍການສິນຄ້າ</h3>
            {!lockStructure ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setPickerOpen(true)}
                >
                  <Plus data-icon="inline-start" />
                  ເພີ່ມສິນຄ້າ
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={addPendingLine}
                >
                  <Plus data-icon="inline-start" />
                  ຍັງບໍ່ມີ SKU
                </Button>
              </div>
            ) : null}
          </div>
          {values.lines.length === 0 ? (
            <button
              type="button"
              className="flex flex-col items-center gap-3 rounded-2xl border border-dashed px-4 py-10"
              onClick={() => !lockStructure && setPickerOpen(true)}
            >
              <Package />
              <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການ</p>
            </button>
          ) : (
            <ul className="flex flex-col gap-3">
              {values.lines.map((line) => {
                if (isPendingImportLineForm(line)) {
                  return (
                    <li
                      key={line.lineKey}
                      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div>
                          <Badge variant="secondary" className="text-[10px]">
                            ຍັງບໍ່ມີ SKU
                          </Badge>
                          <p className="mt-1 text-muted-foreground text-xs">
                            ຊື່ ແລະ ລິ້ງຈາກ Taobao / 1688
                          </p>
                        </div>
                        {!lockStructure ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLine(line.lineKey)}
                          >
                            <TrashIcon />
                          </Button>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-3">
                        <div>
                          <Label className="text-xs">ຊື່ຈາກແອບຈີນ *</Label>
                          <Input
                            value={line.externalTitle}
                            disabled={readOnlyLines}
                            placeholder="ຄັດລອກຊື່ຈາກ Taobao / 1688"
                            onChange={(e) =>
                              updateLine(line.lineKey, {
                                externalTitle: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">ລິ້ງ (ຖ້າມີ)</Label>
                          <Input
                            value={line.externalUrl}
                            disabled={readOnlyLines}
                            placeholder="https://..."
                            onChange={(e) =>
                              updateLine(line.lineKey, {
                                externalUrl: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <Label className="text-xs">ຈຳນວນ</Label>
                            <Input
                              type="number"
                              min={1}
                              disabled={lockQuantity}
                              value={line.orderedQuantity || ""}
                              onChange={(e) =>
                                updateLine(line.lineKey, {
                                  orderedQuantity: Math.max(
                                    1,
                                    Math.trunc(Number(e.target.value) || 0),
                                  ),
                                })
                              }
                            />
                          </div>
                          <div>
                            <Label className="text-xs">
                              ລາຄາ ({priceUnit})
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              disabled={lockPrices}
                              value={line.unitPriceForeign || ""}
                              onChange={(e) =>
                                updateLine(line.lineKey, {
                                  unitPriceForeign: Math.max(
                                    0,
                                    Number(e.target.value) || 0,
                                  ),
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }

                if (!isMatchedImportLineForm(line)) return null;

                const hasAltUnit = usesAlternateReceiveUnit(line);
                const receiveUnit =
                  line.receiveUnitLabel?.trim() || line.stockUnitLabel;
                const stockQty = convertReceiveQtyToStock(
                  line.orderedQuantity,
                  line.receiveConversionFactor,
                );

                return (
                  <li
                    key={line.lineKey}
                    className="rounded-xl border border-secondary/25 p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="size-12 shrink-0 overflow-hidden rounded-lg bg-muted/40">
                        {line.imageKey ? (
                          <AppImage
                            src={line.imageKey}
                            alt={line.productName}
                            className="size-full"
                            fit="cover"
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center">
                            <ImageIcon className="opacity-40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">{line.productName}</p>
                        <p className="text-muted-foreground text-xs">
                          {line.variantName}
                        </p>
                      </div>
                      {!lockStructure ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLine(line.lineKey)}
                        >
                          <TrashIcon />
                        </Button>
                      ) : null}
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs">
                        ຊື່ຈາກ Taobao / 1688 (ຖ້າມີ)
                      </Label>
                      <Input
                        value={line.description}
                        disabled={readOnlyLines}
                        placeholder="ຄັດລອກຊື່ຈາກແອບຈີນເພື່ອອ້າງອີງ"
                        onChange={(e) =>
                          updateLine(line.lineKey, {
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">
                          ຈຳນວນ ({receiveUnit})
                        </Label>
                        <Input
                          type="number"
                          min={line.minOrderedQuantity ?? 1}
                          disabled={lockQuantity}
                          value={line.orderedQuantity || ""}
                          onChange={(e) =>
                            updateLine(line.lineKey, {
                              orderedQuantity: Math.max(
                                line.minOrderedQuantity ?? 1,
                                Math.trunc(Number(e.target.value) || 0),
                              ),
                            })
                          }
                        />
                        {hasAltUnit ? (
                          <p className="text-muted-foreground text-xs">
                            →{" "}
                            {formatQtyWithUnit(stockQty, line.stockUnitLabel)}{" "}
                            ເຂົ້າສະຕັອກ
                          </p>
                        ) : null}
                        {line.minOrderedQuantity &&
                        line.minOrderedQuantity > 1 ? (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            ຂັ້ນຕ່ຳ {line.minOrderedQuantity} (ຮັບ/ຂາດແລ້ວ)
                          </p>
                        ) : null}
                      </div>
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <Label className="text-xs">
                            ລາຄາ/{receiveUnit} ({priceUnit})
                          </Label>
                          {line.priceFromHistory ? (
                            <Badge variant="secondary" className="text-[10px]">
                              ຈາກຄັ້ງກ່ອນ
                            </Badge>
                          ) : null}
                        </div>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          disabled={lockPrices}
                          value={line.unitPriceForeign || ""}
                          onChange={(e) =>
                            updateLine(line.lineKey, {
                              unitPriceForeign: Math.max(
                                0,
                                Number(e.target.value) || 0,
                              ),
                              priceFromHistory: false,
                            })
                          }
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="brand-card">
        <CardContent className="flex flex-col gap-4 px-4 py-0 sm:px-5">
          <h3 className="font-display text-lg">ຕົ້ນທຸນ ແລະ ຂົນສົ່ງ</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="shippingTotalLak">ຄ່າຂົນສົ່ງລວມ (LAK)</Label>
              <Input
                id="shippingTotalLak"
                type="number"
                min={0}
                value={values.shippingTotalLak || ""}
                onChange={(e) =>
                  patch({
                    shippingTotalLak: Math.max(0, Number(e.target.value) || 0),
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="trackingNo">Tracking (ຖ້າມີ)</Label>
              <Input
                id="trackingNo"
                value={values.trackingNo}
                onChange={(e) => patch({ trackingNo: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="carrierName">ຊື່ຂົນສົ່ງ</Label>
              <Input
                id="carrierName"
                value={values.carrierName}
                onChange={(e) => patch({ carrierName: e.target.value })}
              />
            </div>
          </div>
          {previewMutation.isPending ? (
            <Skeleton className="h-20 rounded-xl" />
          ) : preview ? (
            <div className="grid gap-2 rounded-xl border border-secondary/20 bg-secondary/5 p-4 text-sm sm:grid-cols-3">
              <div>
                <p className="text-muted-foreground text-xs">ມູນຄ່າສິນຄ້າ (LAK)</p>
                <p className="font-medium tabular-nums">
                  {formatLakAmount(preview.goodsSubtotalLak)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ຂົນສົ່ງ</p>
                <p className="font-medium tabular-nums">
                  {formatLakAmount(preview.shippingTotalLak)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Landed ລວມ</p>
                <p className="font-semibold tabular-nums">
                  {formatLakAmount(preview.landedTotalLak)}
                </p>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="brand-card">
        <CardContent className="flex flex-col gap-4 px-4 py-0 sm:px-5">
          <label
            htmlFor="mark-ordered-p13"
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-xl border p-3",
              values.markOrdered && "border-primary/40 bg-primary/10",
            )}
          >
            <Checkbox
              id="mark-ordered-p13"
              checked={values.markOrdered}
              onCheckedChange={(c) => patch({ markOrdered: Boolean(c) })}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-sm">ສັ່ງແລ້ວ</p>
              <p className="text-muted-foreground text-xs">
                ຂ້າມສະຖານະຮ່າງ — ໃຊ້ເມື່ອສັ່ງໃນແອບແລ້ວ
              </p>
            </div>
          </label>
          <Separator />
          <Button
            type="button"
            className="w-full rounded-full"
            disabled={!canSubmit}
            isLoading={submitting}
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </CardContent>
      </Card>

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePick}
        allowZeroStock
        purpose="receive"
        excludeBundles
        subtitle="ເລືອກສິນຄ້າທີ່ຈະນຳເຂົ້າ — ບໍ່ຮອງຮັບເຊັດ"
        onCreateProduct={
          productCreateReturnTo ? handleCreateProduct : undefined
        }
        createProductLabel="ສ້າງສິນຄ້າໃໝ່ ແລ້ວກັບມາ"
      />
    </div>
  );
}
